import { useState } from "react";

import CopyToClipboard from "react-copy-to-clipboard";
import { BsLink45Deg } from "react-icons/bs";
import classNames from "classnames";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import SocialShareIcons from "../../../components/common/icons/SocialShareIcons";
import WebShareIcon from "../../../components/common/icons/WebShareIcon";
import NotifyToastContainer, {
  notify,
} from "../../../components/common/NotifyToastContainer";

import TextNavBackground from "../../../components/common/TextNavBackground";
import { apiWrapper } from "../../../utils/api/apiWrapper";
import {
  kTechnicalTeamMemberDetailURL,
  kTechnicalTeamMembersURL,
} from "../../../utils/constants/apiUrls";
import { TWELVE_HOURS_REFRESH_INTERVAL_SECONDS } from "../../../utils/constants/numbers";
import { customSeoConfig, getApiToken } from "../../../utils/helpers/helpers";
import PeopleBioBgStyle from "../../../components/elements/people/PeopleBioBgStyle";
import PeopleBioAvatar from "../../../components/elements/people/PeopleBioAvatar";

const TechnicalMemberProfile = ({ member }) => {
  const memberPublicUrl = member.public_url;

  const [value, setValue] = useState(memberPublicUrl);
  const [copied, setCopied] = useState(false);

  const memberJobTitle = member.job_title;
  const memberFullName = `${member.first_name} ${member.last_name}`;
  const memberBioCaption = member.biography.slice(0, 120);
  const memberSeoTitle = `${memberFullName} | ${memberJobTitle}`;
  const memberAvatar = member.profile_picture;
  const memberBiography = member.biography;

  return (
    <>
      {customSeoConfig(
        memberSeoTitle,
        memberBioCaption,
        memberAvatar,
        memberPublicUrl,
        "website",
      )}

      <TextNavBackground title={memberFullName} />

      <div className="overflow-hidden">
        <div className="relative max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="hidden lg:block bg-gray-50 absolute top-0 bottom-0 mx-auto" />
          <div className="mx-auto text-base max-w-prose">
            <div>
              <h2 className="text-base text-red-600 dark:text-green-800 font-semibold tracking-wide uppercase">
                {memberJobTitle}
              </h2>
              <h3 className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                {memberFullName}
              </h3>
              <div className="flex justify-center items-center space-x-2 mb-4 mt-10">
                <SocialShareIcons
                  title={memberFullName}
                  caption={memberBioCaption}
                  url={memberPublicUrl}
                />

                <CopyToClipboard
                  text={value}
                  onCopy={() => {
                    setCopied({ copied: true });
                    setValue(memberPublicUrl);
                  }}
                >
                  <BsLink45Deg
                    onClick={notify}
                    size={30}
                    className={classNames(
                      copied ? "text-green-600" : "",
                      "cursor-pointer text-gray-500 hover:text-black",
                    )}
                  />
                </CopyToClipboard>

                <WebShareIcon
                  title={memberFullName}
                  caption={memberBioCaption}
                  url={memberPublicUrl}
                />
              </div>
            </div>
          </div>
          <div className="mt-8">
            <div className="relative ">
              <PeopleBioBgStyle />
              <PeopleBioAvatar image={memberAvatar} />
            </div>
          </div>
          <div className="mt-8 lg:mt-0">
            <div className="prose prose-indigo text-gray-900 mx-auto">
              <ReactMarkdown className="markdown" remarkPlugins={[remarkGfm]}>
                {memberBiography}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>

      {copied ? <NotifyToastContainer /> : null}
    </>
  );
};

export default TechnicalMemberProfile;

export const getStaticProps = async (context) => {
  const token = getApiToken();
  const memberSlug = context.params.slug;

  const member = await apiWrapper.get(
    kTechnicalTeamMemberDetailURL + `?slug=${memberSlug}`,
    token,
  );

  return {
    props: {
      member: member?.data,
    },
    revalidate: TWELVE_HOURS_REFRESH_INTERVAL_SECONDS,
  };
};

export async function getStaticPaths() {
  const token = getApiToken();
  const members = await apiWrapper.get(kTechnicalTeamMembersURL, token);

  const membersData = members?.data;

  // SAFE FIX: Use optional chaining (?.) and fallbacks (|| []) to protect the spread operations
  const kotokoTechnicalTeamMembers = [
    ...(membersData?.men || []),
    ...(membersData?.women || []),
    ...(membersData?.under_20 || []),
    ...(membersData?.under_17 || []),
  ];

  const paths = kotokoTechnicalTeamMembers.map((member) => ({
    params: { slug: member?.slug || "" },
  }));

  return { paths, fallback: "blocking" };
}
